'use client';

/* eslint-disable @next/next/no-img-element */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import type { MarketingRuntimeConfig } from '../[slug]/types';
import MobileRankingCard from './MobileRankingCard';
import RankingCard from './RankingCard';
import rankingCardStyles from './RankingCard.module.css';
import RankingTabs from './RankingTabs';
import VibeSection from './VibeSection';
import type { BestsellerPageData, BestsellerProduct, BestsellerRankingTab } from './types';
import { loadRankingProductPage, RANKING_LOAD_BATCH_SIZE } from './rankingProductLoader';
import { mergeVibeProducts } from './vibeCarousel';
import {
  BESTSELLER_MOBILE_RANKING_CARD_LAYOUT_CLASS_NAMES,
  BESTSELLER_RANKING_CARD_LAYOUT_CLASS_NAMES,
} from './bestsellerStyles';
import {
  BESTSELLER_TRACKING_MODULES,
  createBestsellerProductTrackingHandlers,
} from './bestsellerTracking';
import { useBestsellerTracking } from './useBestsellerTracking';

interface BestsellersClientPageProps {
  data: BestsellerPageData;
  deviceType: EPlatform;
  runtimeConfig: MarketingRuntimeConfig;
}

type RankingLoadStatus = 'idle' | 'loading' | 'loaded' | 'empty' | 'error';

interface RankingLoadState {
  products: BestsellerProduct[];
  total: number;
  nextPage: number;
  hasMore: boolean;
  status: RankingLoadStatus;
}

const RANKING_RETURN_STATE_VERSION = 3;
const RANKING_RETURN_STATE_HISTORY_KEY = '__bestsellerRankingReturnStateKey';
const RANKING_RETURN_STATE_TTL_MS = 30 * 60 * 1000;
const RANKING_PAGE_CACHE_VERSION = 1;

interface RankingReturnState {
  version: typeof RANKING_RETURN_STATE_VERSION;
  activeTabId: string;
  targetKey: string;
  viewportTop?: number;
  emptyTabIds?: string[];
  savedAt: number;
}

interface RankingPageCache {
  version: typeof RANKING_PAGE_CACHE_VERSION;
  activeTabId: string;
  rankingLoadStates: Record<string, RankingLoadState>;
  savedAt: number;
}

function getRankingReturnStateStorageKey(slug: string) {
  return `bestsellers:ranking-return:${slug || 'default'}`;
}

function getRankingPageCacheKey(slug: string, currencyName: string, isWeb: boolean) {
  return `campaigns-bestsellers@${slug || 'default'}@${currencyName}@${isWeb ? 'web' : 'wap'}`;
}

function getRankingProductReturnTargetKey(tabId: string, product: BestsellerProduct) {
  return `${tabId}:${product.shopSku || product.id}`;
}

function findRankingReturnTarget(section: HTMLElement | null, targetKey: string) {
  return Array.from(
    section?.querySelectorAll<HTMLElement>('[data-ranking-product-return-target]') || [],
  ).find((element) => element.dataset.rankingProductReturnTarget === targetKey);
}

function scheduleRankingViewportRestore(
  section: HTMLElement,
  targetKey: string,
  viewportTop: number,
  onComplete: () => void,
) {
  const restorePosition = () => {
    const target = findRankingReturnTarget(section, targetKey);
    if (!target) return;

    const offset = target.getBoundingClientRect().top - viewportTop;
    if (Math.abs(offset) < 0.5) return;
    window.scrollTo({
      top: Math.max(0, window.scrollY + offset),
      left: 0,
      behavior: 'auto',
    });
  };

  restorePosition();
  let secondFrame = 0;
  const firstFrame = window.requestAnimationFrame(() => {
    restorePosition();
    secondFrame = window.requestAnimationFrame(restorePosition);
  });
  const firstTimer = window.setTimeout(restorePosition, 100);
  const secondTimer = window.setTimeout(() => {
    restorePosition();
    onComplete();
  }, 300);

  return () => {
    window.cancelAnimationFrame(firstFrame);
    if (secondFrame) window.cancelAnimationFrame(secondFrame);
    window.clearTimeout(firstTimer);
    window.clearTimeout(secondTimer);
  };
}

function isPageReloadNavigation() {
  if (typeof window === 'undefined' || typeof window.performance?.getEntriesByType !== 'function') {
    return false;
  }
  const navigationEntry = window.performance.getEntriesByType('navigation').at(0) as
    | PerformanceNavigationTiming
    | undefined;
  return navigationEntry?.type === 'reload';
}

function isValidRankingPageCache(value: unknown): value is RankingPageCache {
  if (!value || typeof value !== 'object') return false;
  const cache = value as Partial<RankingPageCache>;
  return (
    cache.version === RANKING_PAGE_CACHE_VERSION &&
    typeof cache.activeTabId === 'string' &&
    Boolean(cache.rankingLoadStates) &&
    typeof cache.rankingLoadStates === 'object' &&
    typeof cache.savedAt === 'number' &&
    Date.now() - cache.savedAt <= RANKING_RETURN_STATE_TTL_MS
  );
}

function clearRankingReturnState(storageKey: string) {
  try {
    window.sessionStorage.removeItem(storageKey);
    const historyState = window.history.state;
    if (!historyState || typeof historyState !== 'object') return;

    const nextHistoryState = { ...historyState } as Record<string, unknown>;
    delete nextHistoryState[RANKING_RETURN_STATE_HISTORY_KEY];
    window.history.replaceState(nextHistoryState, document.title, window.location.href);
  } catch {
    // Return-state cleanup must never block page rendering.
  }
}

function parseRankingReturnState(storageKey: string): RankingReturnState | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RankingReturnState>;
    if (
      parsed.version !== RANKING_RETURN_STATE_VERSION ||
      !parsed.activeTabId ||
      !parsed.targetKey ||
      !parsed.savedAt ||
      Date.now() - parsed.savedAt > RANKING_RETURN_STATE_TTL_MS
    ) {
      return null;
    }
    return parsed as RankingReturnState;
  } catch {
    return null;
  }
}

function useMobileRankingLayout(deviceType: EPlatform) {
  const [isMobile, setIsMobile] = useState(deviceType !== EPlatform.WEB);
  const [isResolved, setIsResolved] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      setIsResolved(true);
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const syncLayout = () => setIsMobile(mediaQuery.matches);
    syncLayout();
    setIsResolved(true);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncLayout);
      return () => mediaQuery.removeEventListener('change', syncLayout);
    }

    mediaQuery.addListener(syncLayout);
    return () => mediaQuery.removeListener(syncLayout);
  }, []);

  return { isMobile, isResolved };
}

function readRankingReturnState(storageKey: string): RankingReturnState | null {
  if (typeof window === 'undefined') return null;
  if (isPageReloadNavigation()) {
    clearRankingReturnState(storageKey);
    return null;
  }

  const state = parseRankingReturnState(storageKey);
  if (!state) clearRankingReturnState(storageKey);
  return state;
}

function RankingCardSkeleton({ large = false }: { large?: boolean }) {
  if (large) {
    return (
      <div
        className={`${BESTSELLER_RANKING_CARD_LAYOUT_CLASS_NAMES.large} ${rankingCardStyles.largeCard} animate-pulse`}
        aria-hidden="true"
      >
        <div className="flex h-full items-center justify-center bg-white px-[11%]">
          <div className="h-[32%] w-full rounded-[4px] bg-[#e7e7e7]" />
        </div>
        <div className="h-full bg-[#e7e7e7]" />
        <div
          className={`relative flex h-full min-w-0 flex-col bg-white ${rankingCardStyles.productInfo}`}
        >
          <div className="absolute right-[20px] top-[20px] h-[19px] w-[19px] rounded-full bg-[#eeeeee] xl:right-[24px] xl:top-[24px] xl:h-[24px] xl:w-[24px] 2xl:right-[36px] 2xl:top-[36px] 2xl:h-[32px] 2xl:w-[32px]" />
          <div className="mt-auto space-y-[8px]">
            <div className="h-[14px] w-[58%] rounded bg-[#e7e7e7] xl:h-[18px] 2xl:h-[24px]" />
            <div className="h-[12px] w-[46%] rounded bg-[#eeeeee] xl:h-[14px] 2xl:h-[18px]" />
          </div>
          <div
            className={`${rankingCardStyles.priceBlock} space-y-[8px] xl:space-y-[10px] 2xl:space-y-[16px]`}
          >
            <div className="h-[14px] w-[70%] rounded bg-[#e7e7e7] xl:h-[18px] 2xl:h-[24px]" />
            <div
              className={`${rankingCardStyles.purchasedPill} w-[64%] rounded-[3px] bg-[#eeeeee] 2xl:rounded-[6px]`}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${BESTSELLER_RANKING_CARD_LAYOUT_CLASS_NAMES.compact} ${rankingCardStyles.compactCard} animate-pulse`}
      aria-hidden="true"
    >
      <div className="flex h-full items-center justify-center bg-white px-[11%]">
        <div className="h-[32%] w-full rounded-[4px] bg-[#e7e7e7]" />
      </div>
      <div
        className={`relative flex h-full min-w-0 flex-col bg-white ${rankingCardStyles.productInfo}`}
      >
        <div className="absolute right-[10px] top-[11px] h-[16px] w-[16px] rounded-full bg-[#eeeeee] xl:right-[9px] xl:top-[12px] xl:h-[20px] xl:w-[20px] 2xl:right-[16px] 2xl:top-[16px] 2xl:h-[32px] 2xl:w-[32px]" />
        <div className="mt-auto space-y-[4px] xl:space-y-[6px] 2xl:space-y-[9px]">
          <div className="h-[10px] w-[58%] rounded bg-[#e7e7e7] xl:h-[14px] 2xl:h-[18px]" />
          <div className="h-[8px] w-[46%] rounded bg-[#eeeeee] xl:h-[10px] 2xl:h-[12px]" />
        </div>
        <div
          className={`${rankingCardStyles.priceBlock} space-y-[6px] xl:space-y-[8px] 2xl:space-y-[12px]`}
        >
          <div className="h-[10px] w-[70%] rounded bg-[#e7e7e7] xl:h-[14px] 2xl:h-[18px]" />
          <div
            className={`${rankingCardStyles.purchasedPill} w-[64%] rounded-[3px] bg-[#eeeeee] 2xl:rounded-[6px]`}
          />
        </div>
      </div>
    </div>
  );
}

function MobileRankingCardSkeleton({ top = false }: { top?: boolean }) {
  if (top) {
    return (
      <div
        className={`${BESTSELLER_MOBILE_RANKING_CARD_LAYOUT_CLASS_NAMES.top} animate-pulse`}
        aria-hidden="true"
      >
        <div className="relative flex items-center justify-center px-[18px] pt-[30px]">
          <div className="h-[70px] w-full rounded-[4px] bg-[#e7e7e7]" />
        </div>
        <div className="absolute inset-y-0 right-0 w-1/2 bg-[#e7e7e7]" />
        <div className="col-start-1 row-start-2 space-y-[7px] bg-white px-[12px] py-[8px]">
          <div className="h-[10px] w-[58%] rounded bg-[#e7e7e7]" />
          <div className="h-[12px] w-[72%] rounded bg-[#eeeeee]" />
          <div className="h-[21px] w-[68%] rounded-[3px] bg-[#eeeeee]" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${BESTSELLER_MOBILE_RANKING_CARD_LAYOUT_CLASS_NAMES.compact} animate-pulse`}
      aria-hidden="true"
    >
      <div className="flex items-center justify-center px-[16px] pt-[30px]">
        <div className="h-[70px] w-full rounded-[4px] bg-[#e7e7e7]" />
      </div>
      <div className="space-y-[7px] bg-white px-[8px] py-[8px]">
        <div className="h-[10px] w-[58%] rounded bg-[#e7e7e7]" />
        <div className="h-[12px] w-[72%] rounded bg-[#eeeeee]" />
        <div className="h-[21px] w-[68%] rounded-[3px] bg-[#eeeeee]" />
      </div>
    </div>
  );
}

function createInitialRankingLoadStates(
  data: BestsellerPageData,
): Record<string, RankingLoadState> {
  return Object.fromEntries(
    data.tabs.map((tab) => {
      const initialProducts = data.rankings[tab.id] || [];
      const total = Math.max(tab.total ?? initialProducts.length, initialProducts.length);
      const canLoadProducts = Boolean(tab.productGroupId);
      const initiallyLoaded =
        tab.initiallyLoaded ?? (initialProducts.length > 0 || !canLoadProducts);
      return [
        tab.id,
        {
          products: initialProducts,
          total,
          nextPage:
            initialProducts.length > 0
              ? Math.floor((initialProducts.length - 1) / RANKING_LOAD_BATCH_SIZE) + 2
              : 1,
          hasMore:
            canLoadProducts &&
            (!initiallyLoaded || (tab.hasMore ?? total > initialProducts.length)),
          status: initiallyLoaded ? 'loaded' : 'idle',
        },
      ];
    }),
  );
}

function createRestoredRankingLoadStates(
  data: BestsellerPageData,
  pageCache: RankingPageCache | null,
): Record<string, RankingLoadState> {
  const initialStates = createInitialRankingLoadStates(data);
  if (!pageCache) return initialStates;

  return Object.fromEntries(
    data.tabs.map((tab) => {
      const initialState = initialStates[tab.id];
      const cachedState = pageCache.rankingLoadStates[tab.id];
      if (!initialState || !cachedState) return [tab.id, initialState];

      if (cachedState.products.length <= initialState.products.length) {
        return [tab.id, initialState];
      }

      const products = cachedState.products;

      return [
        tab.id,
        {
          ...cachedState,
          products,
          total: Math.max(initialState.total, cachedState.total, products.length),
          status:
            cachedState.status === 'loading'
              ? products.length > 0
                ? 'loaded'
                : 'idle'
              : cachedState.status,
        } satisfies RankingLoadState,
      ];
    }),
  );
}

function replaceRefreshedRankingPage(
  current: BestsellerProduct[],
  refreshed: BestsellerProduct[],
  pageNumber: number,
  hasMore: boolean,
) {
  const pageStart = (pageNumber - 1) * RANKING_LOAD_BATCH_SIZE;
  const nextProducts = [
    ...current.slice(0, pageStart),
    ...refreshed,
    ...current.slice(pageStart + RANKING_LOAD_BATCH_SIZE),
  ];

  return hasMore ? nextProducts : nextProducts.slice(0, pageStart + refreshed.length);
}

export default function BestsellersClientPage({
  data,
  deviceType,
  runtimeConfig,
}: BestsellersClientPageProps) {
  const isApp = deviceType === EPlatform.ANDROID || deviceType === EPlatform.IOS;
  const currencyName = useStore((state) => state.currency.name);
  const getPageCache = useStore((state) => state.getPageCache);
  const setPageCache = useStore((state) => state.setPageCache);
  const clearPageCache = useStore((state) => state.clearPageCache);
  const { isMobile: isMobileRankingLayout, isResolved: isRankingLayoutResolved } =
    useMobileRankingLayout(deviceType);
  const rankingReturnStateStorageKey = getRankingReturnStateStorageKey(runtimeConfig.slug);
  const rankingPageCacheKey = getRankingPageCacheKey(
    runtimeConfig.slug,
    currencyName,
    deviceType === EPlatform.WEB,
  );
  const initialRankingPageCache = useMemo(() => {
    if (isPageReloadNavigation() || !parseRankingReturnState(rankingReturnStateStorageKey)) {
      return null;
    }
    const pageCache = getPageCache(rankingPageCacheKey) as unknown;
    return isValidRankingPageCache(pageCache) ? pageCache : null;
  }, [getPageCache, rankingPageCacheKey, rankingReturnStateStorageKey]);
  const restoredScrollTargetRef = useRef('');
  const restoredViewportTopRef = useRef<number | null>(null);
  const rankingViewportAnchorRef = useRef<{
    targetKey: string;
    viewportTop: number;
    tabId: string;
    isMobileLayout: boolean;
  } | null>(null);
  const rankingScrollRestoreCleanupRef = useRef<() => void>(() => undefined);
  const restoredRevalidationTargetRef = useRef('');
  const restoredRankingLoadAttemptRef = useRef('');
  const restoredReturnStateAppliedRef = useRef(false);
  const activeRankingSectionRef = useRef<HTMLElement | null>(null);
  const [selectedRankingTab, setSelectedRankingTab] = useState(() =>
    initialRankingPageCache &&
    data.tabs.some((tab) => tab.id === initialRankingPageCache.activeTabId)
      ? initialRankingPageCache.activeTabId
      : data.defaultRankingTabId || data.tabs[0]?.id || 'classic',
  );
  const [rankingLoadStates, setRankingLoadStates] = useState<Record<string, RankingLoadState>>(() =>
    createRestoredRankingLoadStates(data, initialRankingPageCache),
  );
  const [isRestoredRankingRevalidating, setIsRestoredRankingRevalidating] = useState(() =>
    Boolean(initialRankingPageCache),
  );
  const [rankingWishStates, setRankingWishStates] = useState<Record<string, number>>({});
  const loadingRankingIds = useRef(new Set<string>());
  const exposedRankingProductsRef = useRef(new Set<string>());
  const rankingLoadSentinelRef = useRef<HTMLDivElement | null>(null);
  const visibleRankingTabs = useMemo(
    () =>
      data.tabs.filter((tab) => {
        const loadState = rankingLoadStates[tab.id];
        return loadState?.status !== 'empty';
      }),
    [data.tabs, rankingLoadStates],
  );
  const activeRankingTab = visibleRankingTabs.some((tab) => tab.id === selectedRankingTab)
    ? selectedRankingTab
    : visibleRankingTabs[0]?.id || '';
  const activeRankingLoadState = rankingLoadStates[activeRankingTab];
  const rankingProducts = useMemo(
    () =>
      (activeRankingLoadState?.products || []).map((product) => {
        const isWish = rankingWishStates[product.shopSku];
        return isWish === undefined ? product : { ...product, isWish };
      }),
    [activeRankingLoadState?.products, rankingWishStates],
  );
  const leadingProducts = rankingProducts.slice(0, 3);
  const restProducts = rankingProducts.slice(3);
  const activeRankingTabData = visibleRankingTabs.find((tab) => tab.id === activeRankingTab);
  const rankingSubtitle = (activeRankingTabData?.eyebrow || data.rankingNote || '').trim();
  const rankingBackgroundColor = data.rankingBackgroundColor || '#f4f4f4';
  const heroTitleImage = data.hero.pcTitleImage || data.hero.wapTitleImage;
  const heroBannerHeightClassName = heroTitleImage
    ? 'h-auto'
    : 'h-[158px] md:h-[140px] xl:h-[172px] 2xl:h-[260px]';
  const heroTitleHeightClassName = heroTitleImage
    ? 'h-auto'
    : 'h-[54px] md:h-[82px] xl:h-[87px] 2xl:h-[131px]';
  const isInitialRankingLoading =
    rankingProducts.length === 0 &&
    (activeRankingLoadState?.status === 'idle' || activeRankingLoadState?.status === 'loading');
  const isAppendingRankingProducts =
    rankingProducts.length > 0 && activeRankingLoadState?.status === 'loading';
  const shouldRenderRankingModule = visibleRankingTabs.length > 0;
  const {
    trackPageView,
    trackPageLeave,
    trackProductListPitsClick,
    trackProductListPitsExposure,
    trackTopBannerAdExposure,
    trackCollectButtonClick,
  } = useBestsellerTracking(runtimeConfig);
  const productTracking = useMemo(
    () => ({
      trackProductListPitsClick,
      trackProductListPitsExposure,
      trackCollectButtonClick,
    }),
    [trackCollectButtonClick, trackProductListPitsClick, trackProductListPitsExposure],
  );
  const { ref: bannerExposureRef } = useValidExposure({
    callback: () => trackTopBannerAdExposure(data.hero.trackingJumpContent, 0),
    ts: data.hero.pcTitleImage || data.hero.wapTitleImage || data.hero.title,
  });

  useEffect(() => {
    trackPageView();
    let appPageActive = true;

    const handleAppView = () => {
      if (appPageActive) return;
      appPageActive = true;
      trackPageView();
    };
    const handleAppLeave = () => {
      if (!appPageActive) return;
      appPageActive = false;
      trackPageLeave();
    };

    if (isApp) {
      window.addEventListener('appView', handleAppView);
      window.addEventListener('appLeave', handleAppLeave);
    }

    return () => {
      if (isApp) {
        window.removeEventListener('appView', handleAppView);
        window.removeEventListener('appLeave', handleAppLeave);
        handleAppLeave();
        return;
      }
      trackPageLeave();
    };
  }, [isApp, trackPageLeave, trackPageView]);

  useEffect(() => {
    if (restoredReturnStateAppliedRef.current) return;
    restoredReturnStateAppliedRef.current = true;

    const restoredReturnState = readRankingReturnState(rankingReturnStateStorageKey);
    if (
      !restoredReturnState?.activeTabId ||
      !data.tabs.some((tab) => tab.id === restoredReturnState.activeTabId)
    ) {
      return;
    }

    restoredScrollTargetRef.current = restoredReturnState.targetKey;
    restoredViewportTopRef.current =
      typeof restoredReturnState.viewportTop === 'number' ? restoredReturnState.viewportTop : null;
    restoredRevalidationTargetRef.current = restoredReturnState.targetKey;
    if (restoredReturnState.emptyTabIds?.length) {
      const emptyTabIds = new Set(restoredReturnState.emptyTabIds);
      setRankingLoadStates((current) =>
        Object.fromEntries(
          Object.entries(current).map(([tabId, loadState]) => [
            tabId,
            emptyTabIds.has(tabId)
              ? { ...loadState, products: [], total: 0, hasMore: false, status: 'empty' }
              : loadState,
          ]),
        ),
      );
    }
    setSelectedRankingTab(restoredReturnState.activeTabId);
  }, [data, rankingReturnStateStorageKey]);

  useEffect(() => {
    const targetKey = restoredScrollTargetRef.current;
    if (!targetKey || !isRankingLayoutResolved) return;

    const section = activeRankingSectionRef.current;
    const target = findRankingReturnTarget(section, targetKey);
    if (!target) return;

    if (restoredViewportTopRef.current === null || !section) {
      target.scrollIntoView({ block: 'center', inline: 'nearest' });
    } else {
      rankingViewportAnchorRef.current = {
        targetKey,
        viewportTop: restoredViewportTopRef.current,
        tabId: activeRankingTab,
        isMobileLayout: isMobileRankingLayout,
      };
    }
    restoredScrollTargetRef.current = '';
    restoredViewportTopRef.current = null;
    restoredRankingLoadAttemptRef.current = '';
    clearRankingReturnState(rankingReturnStateStorageKey);
    clearPageCache(rankingPageCacheKey);
  }, [
    activeRankingTab,
    clearPageCache,
    isMobileRankingLayout,
    isRankingLayoutResolved,
    rankingPageCacheKey,
    rankingProducts.length,
    rankingReturnStateStorageKey,
  ]);

  useEffect(() => {
    const anchor = rankingViewportAnchorRef.current;
    if (!anchor || !isRankingLayoutResolved) return;

    if (anchor.tabId !== activeRankingTab || anchor.isMobileLayout !== isMobileRankingLayout) {
      rankingViewportAnchorRef.current = null;
      return;
    }

    const section = activeRankingSectionRef.current;
    const target = findRankingReturnTarget(section, anchor.targetKey);
    if (!section || !target) {
      if (!isRestoredRankingRevalidating) rankingViewportAnchorRef.current = null;
      return;
    }

    const cleanup = scheduleRankingViewportRestore(
      section,
      anchor.targetKey,
      anchor.viewportTop,
      () => {
        if (!isRestoredRankingRevalidating && rankingViewportAnchorRef.current === anchor) {
          rankingViewportAnchorRef.current = null;
        }
      },
    );
    rankingScrollRestoreCleanupRef.current = cleanup;

    return () => {
      cleanup();
      if (rankingScrollRestoreCleanupRef.current === cleanup) {
        rankingScrollRestoreCleanupRef.current = () => undefined;
      }
    };
  }, [
    activeRankingLoadState?.products,
    activeRankingTab,
    isMobileRankingLayout,
    isRankingLayoutResolved,
    isRestoredRankingRevalidating,
  ]);

  useEffect(
    () => () => {
      rankingScrollRestoreCleanupRef.current();
    },
    [],
  );

  const saveRankingReturnState = useCallback(
    (product: BestsellerProduct) => {
      if (typeof window === 'undefined') return;
      const targetKey = getRankingProductReturnTargetKey(activeRankingTab, product);
      const target = findRankingReturnTarget(activeRankingSectionRef.current, targetKey);
      const viewportTop = target?.getBoundingClientRect().top;
      const returnState: RankingReturnState = {
        version: RANKING_RETURN_STATE_VERSION,
        activeTabId: activeRankingTab,
        targetKey,
        viewportTop: Number.isFinite(viewportTop) ? viewportTop : undefined,
        emptyTabIds: Object.entries(rankingLoadStates)
          .filter(([, loadState]) => loadState.status === 'empty')
          .map(([tabId]) => tabId),
        savedAt: Date.now(),
      };

      try {
        window.sessionStorage.setItem(rankingReturnStateStorageKey, JSON.stringify(returnState));
        const historyState = window.history.state;
        window.history.replaceState(
          {
            ...(historyState && typeof historyState === 'object' ? historyState : {}),
            [RANKING_RETURN_STATE_HISTORY_KEY]: rankingReturnStateStorageKey,
          },
          document.title,
          window.location.href,
        );
        const activeLoadState = rankingLoadStates[activeRankingTab];
        if (activeLoadState) {
          setPageCache(rankingPageCacheKey, {
            version: RANKING_PAGE_CACHE_VERSION,
            activeTabId: activeRankingTab,
            rankingLoadStates: { [activeRankingTab]: activeLoadState },
            savedAt: returnState.savedAt,
          } satisfies RankingPageCache);
        }
      } catch {
        clearRankingReturnState(rankingReturnStateStorageKey);
        clearPageCache(rankingPageCacheKey);
        // Ignore storage failures; navigation and tracking should continue normally.
      }
    },
    [
      activeRankingTab,
      rankingLoadStates,
      rankingPageCacheKey,
      rankingReturnStateStorageKey,
      clearPageCache,
      setPageCache,
    ],
  );

  const loadMoreRankingProducts = useCallback(
    async (tab: BestsellerRankingTab) => {
      const loadState = rankingLoadStates[tab.id];
      if (
        !loadState ||
        !tab.productGroupId ||
        loadState.status === 'loading' ||
        (loadState.status === 'loaded' && !loadState.hasMore) ||
        loadingRankingIds.current.has(tab.id)
      ) {
        return;
      }

      loadingRankingIds.current.add(tab.id);
      setRankingLoadStates((current) => ({
        ...current,
        [tab.id]: { ...current[tab.id], status: 'loading' },
      }));

      try {
        const page = await loadRankingProductPage(tab, loadState.nextPage);
        setRankingLoadStates((current) => {
          const currentState = current[tab.id];
          const pageStart = (loadState.nextPage - 1) * RANKING_LOAD_BATCH_SIZE;
          const isReplacingCachedPage = currentState.products.length > pageStart;
          const products =
            loadState.nextPage === 1
              ? page.products
              : isReplacingCachedPage
                ? replaceRefreshedRankingPage(
                    currentState.products,
                    page.products,
                    loadState.nextPage,
                    page.hasMore,
                  )
                : mergeVibeProducts(currentState.products, page.products);
          const confirmedEmpty = loadState.nextPage === 1 && products.length === 0 && !page.hasMore;
          return {
            ...current,
            [tab.id]: {
              ...currentState,
              products,
              total: Math.max(page.total ?? currentState.total, products.length),
              nextPage: loadState.nextPage + 1,
              hasMore: page.hasMore,
              status: confirmedEmpty ? 'empty' : 'loaded',
            },
          };
        });
      } catch (error) {
        logger.error('Failed to lazy-load bestseller ranking products:', error);
        setRankingLoadStates((current) => ({
          ...current,
          [tab.id]: {
            ...current[tab.id],
            hasMore: current[tab.id]?.hasMore ?? true,
            status: 'error',
          },
        }));
      } finally {
        loadingRankingIds.current.delete(tab.id);
      }
    },
    [rankingLoadStates],
  );

  useEffect(() => {
    if (!activeRankingTabData || activeRankingLoadState?.status !== 'idle') return;
    void loadMoreRankingProducts(activeRankingTabData);
  }, [activeRankingLoadState?.status, activeRankingTabData, loadMoreRankingProducts]);

  useEffect(() => {
    const pageCache = initialRankingPageCache;
    const tab = data.tabs.find((item) => item.id === pageCache?.activeTabId);
    const cachedState = pageCache?.rankingLoadStates[pageCache.activeTabId];
    if (!pageCache || !tab?.productGroupId || !cachedState || activeRankingTab !== tab.id) {
      setIsRestoredRankingRevalidating(false);
      return;
    }

    const initialProducts = data.rankings[tab.id] || [];
    const lastLoadedPage = Math.max(1, cachedState.nextPage - 1);
    const firstPageToRefresh = initialProducts.length > 0 ? 2 : 1;
    if (firstPageToRefresh > lastLoadedPage) {
      setRankingLoadStates((current) => ({
        ...current,
        [tab.id]: createInitialRankingLoadStates(data)[tab.id],
      }));
      restoredRevalidationTargetRef.current = '';
      setIsRestoredRankingRevalidating(false);
      return;
    }

    let ignore = false;
    const refreshLoadedPages = async () => {
      let commitInitialProducts: () => void = () => undefined;
      try {
        const targetKey = restoredRevalidationTargetRef.current;
        let products = cachedState.products;
        let pageNumber = firstPageToRefresh;
        let pageLimit = Math.max(
          lastLoadedPage,
          Math.ceil(cachedState.total / RANKING_LOAD_BATCH_SIZE),
        );
        let lastPage: Awaited<ReturnType<typeof loadRankingProductPage>> | undefined;
        let initialProductsApplied = false;

        const commitRefreshedProducts = (
          refreshedProducts: BestsellerProduct[],
          refreshedPageNumber: number,
          refreshedPage: Pick<
            Awaited<ReturnType<typeof loadRankingProductPage>>,
            'total' | 'hasMore'
          >,
        ) => {
          products = replaceRefreshedRankingPage(
            products,
            refreshedProducts,
            refreshedPageNumber,
            refreshedPage.hasMore,
          );
          const committedProducts = products;
          setRankingLoadStates((current) => {
            const currentState = current[tab.id] || cachedState;
            const confirmedEmpty = committedProducts.length === 0 && !refreshedPage.hasMore;
            return {
              ...current,
              [tab.id]: {
                ...currentState,
                products: committedProducts,
                total: Math.max(
                  refreshedPage.total ?? currentState.total,
                  committedProducts.length,
                ),
                nextPage: refreshedPageNumber + 1,
                hasMore: refreshedPage.hasMore,
                status: confirmedEmpty ? 'empty' : 'loaded',
              },
            };
          });
        };

        const applyInitialProducts = () => {
          if (initialProductsApplied || initialProducts.length === 0) return;
          products = replaceRefreshedRankingPage(
            products,
            initialProducts,
            1,
            tab.hasMore ?? cachedState.hasMore,
          );
          initialProductsApplied = true;
        };

        commitInitialProducts = () => {
          if (initialProductsApplied || initialProducts.length === 0) return;
          initialProductsApplied = true;
          commitRefreshedProducts(initialProducts, 1, {
            total: tab.total,
            hasMore: tab.hasMore ?? cachedState.hasMore,
          });
        };

        while (
          pageNumber <= lastLoadedPage ||
          (Boolean(targetKey) &&
            !products.some(
              (product) => getRankingProductReturnTargetKey(tab.id, product) === targetKey,
            ) &&
            Boolean(lastPage?.hasMore) &&
            pageNumber <= pageLimit)
        ) {
          const refreshedPage = await loadRankingProductPage(tab, pageNumber);
          if (ignore) return;
          applyInitialProducts();
          lastPage = refreshedPage;
          pageLimit = Math.max(
            pageLimit,
            Math.ceil((refreshedPage.total ?? products.length) / RANKING_LOAD_BATCH_SIZE),
          );
          commitRefreshedProducts(refreshedPage.products, pageNumber, refreshedPage);
          pageNumber += 1;
        }
      } catch (error) {
        if (!ignore) commitInitialProducts();
        logger.error('Failed to refresh restored bestseller ranking products:', error);
      } finally {
        if (!ignore) {
          restoredRevalidationTargetRef.current = '';
          setIsRestoredRankingRevalidating(false);
        }
      }
    };

    void refreshLoadedPages();
    return () => {
      ignore = true;
    };
  }, [activeRankingTab, data, initialRankingPageCache]);

  useEffect(() => {
    const targetKey = restoredScrollTargetRef.current;
    if (
      !targetKey ||
      !isRankingLayoutResolved ||
      !activeRankingTabData ||
      !activeRankingLoadState ||
      !targetKey.startsWith(`${activeRankingTab}:`)
    ) {
      return;
    }

    const targetIsLoaded = rankingProducts.some(
      (product) => getRankingProductReturnTargetKey(activeRankingTab, product) === targetKey,
    );
    if (targetIsLoaded || activeRankingLoadState.status === 'idle') return;

    if (activeRankingLoadState.hasMore && activeRankingLoadState.status !== 'loading') {
      const attemptKey = `${activeRankingTab}:${activeRankingLoadState.nextPage}`;
      if (restoredRankingLoadAttemptRef.current === attemptKey) return;
      restoredRankingLoadAttemptRef.current = attemptKey;
      void loadMoreRankingProducts(activeRankingTabData);
      return;
    }

    if (
      !activeRankingLoadState.hasMore &&
      activeRankingLoadState.status !== 'loading' &&
      activeRankingLoadState.status !== 'error'
    ) {
      restoredScrollTargetRef.current = '';
      restoredRankingLoadAttemptRef.current = '';
      clearRankingReturnState(rankingReturnStateStorageKey);
      clearPageCache(rankingPageCacheKey);
    }
  }, [
    activeRankingLoadState,
    activeRankingTab,
    activeRankingTabData,
    clearPageCache,
    isRankingLayoutResolved,
    loadMoreRankingProducts,
    rankingProducts,
    rankingPageCacheKey,
    rankingReturnStateStorageKey,
  ]);

  useEffect(() => {
    const sentinel = rankingLoadSentinelRef.current;
    if (
      !sentinel ||
      isRestoredRankingRevalidating ||
      !activeRankingTabData ||
      activeRankingLoadState?.status !== 'loaded' ||
      !activeRankingLoadState.hasMore ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void loadMoreRankingProducts(activeRankingTabData);
        }
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    activeRankingLoadState,
    activeRankingTabData,
    isRestoredRankingRevalidating,
    loadMoreRankingProducts,
  ]);

  const handleRankingTabChange = (tabId: string) => {
    if (tabId !== activeRankingTab) {
      rankingScrollRestoreCleanupRef.current();
      rankingViewportAnchorRef.current = null;
    }
    if (restoredScrollTargetRef.current && tabId !== activeRankingTab) {
      restoredScrollTargetRef.current = '';
      restoredRankingLoadAttemptRef.current = '';
      clearRankingReturnState(rankingReturnStateStorageKey);
      clearPageCache(rankingPageCacheKey);
    }
    setSelectedRankingTab(tabId);
    setRankingLoadStates((current) => {
      const loadState = current[tabId];
      if (!loadState || loadState.status !== 'error') return current;
      return { ...current, [tabId]: { ...loadState, status: 'idle' } };
    });
  };

  const createRankingTrackingHandlers = (
    product: (typeof rankingProducts)[number],
    index: number,
  ) => {
    const handlers = createBestsellerProductTrackingHandlers({
      tracking: productTracking,
      product,
      index,
      moduleName: BESTSELLER_TRACKING_MODULES.ranking,
      tabName: activeRankingTabData?.label || '',
    });
    const exposureKey = getRankingProductReturnTargetKey(activeRankingTab, product);

    return {
      ...handlers,
      onProductExposure: (...args: Parameters<typeof handlers.onProductExposure>) => {
        if (exposedRankingProductsRef.current.has(exposureKey)) return;
        exposedRankingProductsRef.current.add(exposureKey);
        handlers.onProductExposure(...args);
      },
    };
  };

  const handleRankingWishChange = (product: BestsellerProduct, isWish: number) => {
    setRankingWishStates((current) =>
      current[product.shopSku] === isWish ? current : { ...current, [product.shopSku]: isWish },
    );
  };

  return (
    <main className="min-h-screen bg-white">
      <section
        ref={bannerExposureRef}
        className={`relative flex items-center justify-center overflow-hidden bg-white px-[31px] pb-[21px] pt-[32px] text-center md:px-[20px] md:py-[20px] xl:px-[32px] xl:py-[32px] 2xl:items-start 2xl:px-[48px] 2xl:py-[48px] ${heroBannerHeightClassName}`}
      >
        <div className="relative z-[1] w-[313px] max-w-full md:w-[542px] xl:w-[632px] 2xl:w-[903px]">
          <h1
            className={`flex items-center justify-center font-bb text-[40px] leading-[48px] font-bold text-[#282828] md:text-[60px] md:leading-[72px] xl:text-[64px] xl:leading-[77px] 2xl:text-[96px] 2xl:leading-[115px] ${heroTitleHeightClassName}`}
          >
            {heroTitleImage ? (
              <picture className="block w-full">
                {data.hero.wapTitleImage && (
                  <source media="(max-width: 767px)" srcSet={data.hero.wapTitleImage} />
                )}
                <img
                  src={heroTitleImage}
                  alt={data.hero.alt || data.hero.title}
                  className="block h-auto w-full object-contain"
                />
              </picture>
            ) : (
              data.hero.title
            )}
          </h1>
          {data.hero.subtitle ? (
            <p
              data-bestseller-hero-subtitle
              className="mt-[4px] font-br text-[12px] leading-[18px] text-[#282828] md:mt-0 xl:text-[14px] xl:leading-[21px] 2xl:text-[20px] 2xl:leading-[24px]"
            >
              {data.hero.subtitle}
            </p>
          ) : null}
        </div>
      </section>

      {shouldRenderRankingModule ? (
        <>
          <RankingTabs
            tabs={visibleRankingTabs}
            activeTabId={activeRankingTab}
            onChange={handleRankingTabChange}
          />

          {isMobileRankingLayout ? (
            <section
              ref={activeRankingSectionRef}
              className="px-[12px] pb-[40px] pt-[20px]"
              style={{ backgroundColor: rankingBackgroundColor }}
              aria-busy={activeRankingLoadState?.status === 'loading'}
              data-ranking-layout="mobile"
            >
              {rankingSubtitle ? (
                <p
                  className="text-right font-br text-[11px] leading-[14px] text-[#6c6c6c]"
                  data-ranking-subtitle
                >
                  {rankingSubtitle}
                </p>
              ) : null}
              <div
                className={`${rankingSubtitle ? 'mt-[8px]' : ''} flex flex-col gap-[8px]`}
                data-ranking-products
              >
                {isInitialRankingLoading ? (
                  <div data-ranking-skeleton="initial" className="flex flex-col gap-[8px]">
                    {Array.from({ length: 3 }, (_, index) => (
                      <MobileRankingCardSkeleton key={`mobile-leading-${index}`} top />
                    ))}
                    <div className="mt-[4px] grid grid-cols-2 gap-x-[7px] gap-y-[8px]">
                      {Array.from({ length: 6 }, (_, index) => (
                        <MobileRankingCardSkeleton key={`mobile-rest-${index}`} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {leadingProducts.map((product, index) => (
                      <MobileRankingCard
                        key={product.id}
                        product={product}
                        trackingHandlers={createRankingTrackingHandlers(product, index)}
                        exposureKey={`${activeRankingTab}:${product.shopSku}`}
                        returnTargetKey={getRankingProductReturnTargetKey(
                          activeRankingTab,
                          product,
                        )}
                        onBeforeNavigate={() => saveRankingReturnState(product)}
                        onWishChange={(isWish) => handleRankingWishChange(product, isWish)}
                        top
                      />
                    ))}
                    <div className="mt-[4px] grid grid-cols-2 gap-x-[7px] gap-y-[8px]">
                      {restProducts.map((product, index) => (
                        <MobileRankingCard
                          key={product.id}
                          product={product}
                          trackingHandlers={createRankingTrackingHandlers(product, index + 3)}
                          exposureKey={`${activeRankingTab}:${product.shopSku}`}
                          returnTargetKey={getRankingProductReturnTargetKey(
                            activeRankingTab,
                            product,
                          )}
                          onBeforeNavigate={() => saveRankingReturnState(product)}
                          onWishChange={(isWish) => handleRankingWishChange(product, isWish)}
                        />
                      ))}
                      {isAppendingRankingProducts && (
                        <div className="contents" data-ranking-skeleton="append">
                          {Array.from({ length: 2 }, (_, index) => (
                            <MobileRankingCardSkeleton key={`mobile-append-${index}`} />
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </section>
          ) : (
            <section
              ref={activeRankingSectionRef}
              className="px-[48px] pb-[40px] pt-[20px] xl:pb-[60px] xl:pt-[40px] 2xl:px-[47px] 2xl:pb-[80px]"
              style={{ backgroundColor: rankingBackgroundColor }}
              aria-busy={activeRankingLoadState?.status === 'loading'}
              data-ranking-layout="desktop"
            >
              {rankingSubtitle ? (
                <p
                  className="mx-auto max-w-[1825px] text-right font-br text-[14px] leading-[17px] text-[#6c6c6c] 2xl:text-[16px] 2xl:leading-[19px]"
                  data-ranking-subtitle
                >
                  {rankingSubtitle}
                </p>
              ) : null}
              <div
                className={`mx-auto flex max-w-[1825px] flex-col gap-[12px] xl:gap-[16px] 2xl:gap-[20px] ${
                  rankingSubtitle ? 'mt-[12px] xl:mt-[11px] 2xl:mt-[16px]' : ''
                }`}
                data-ranking-products
              >
                {isInitialRankingLoading ? (
                  <div
                    data-ranking-skeleton="initial"
                    className="flex flex-col gap-[12px] xl:gap-[16px]"
                  >
                    {Array.from({ length: 3 }, (_, index) => (
                      <RankingCardSkeleton key={`desktop-leading-${index}`} large />
                    ))}
                    <div className="grid gap-[12px] md:grid-cols-3 xl:gap-[16px]">
                      {Array.from({ length: 6 }, (_, index) => (
                        <RankingCardSkeleton key={`desktop-rest-${index}`} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {leadingProducts.map((product, index) => (
                      <RankingCard
                        key={product.id}
                        product={product}
                        trackingHandlers={createRankingTrackingHandlers(product, index)}
                        exposureKey={`${activeRankingTab}:${product.shopSku}`}
                        returnTargetKey={getRankingProductReturnTargetKey(
                          activeRankingTab,
                          product,
                        )}
                        onBeforeNavigate={() => saveRankingReturnState(product)}
                        onWishChange={(isWish) => handleRankingWishChange(product, isWish)}
                        large
                      />
                    ))}
                    <div className="grid gap-[12px] md:grid-cols-3 xl:gap-[16px] 2xl:gap-x-[16px] 2xl:gap-y-[20px]">
                      {restProducts.map((product, index) => (
                        <RankingCard
                          key={product.id}
                          product={product}
                          trackingHandlers={createRankingTrackingHandlers(product, index + 3)}
                          exposureKey={`${activeRankingTab}:${product.shopSku}`}
                          returnTargetKey={getRankingProductReturnTargetKey(
                            activeRankingTab,
                            product,
                          )}
                          onBeforeNavigate={() => saveRankingReturnState(product)}
                          onWishChange={(isWish) => handleRankingWishChange(product, isWish)}
                        />
                      ))}
                      {isAppendingRankingProducts && (
                        <div className="contents" data-ranking-skeleton="append">
                          {Array.from({ length: 3 }, (_, index) => (
                            <RankingCardSkeleton key={`desktop-append-${index}`} />
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </section>
          )}

          <div
            ref={rankingLoadSentinelRef}
            className="h-px"
            data-ranking-load-sentinel
            aria-hidden="true"
          />
        </>
      ) : null}

      <VibeSection
        title={data.vibeHeading}
        subtitle={data.vibeSubtitle}
        vibes={data.vibes}
        products={data.vibeProducts}
        tracking={productTracking}
      />
    </main>
  );
}
